<template>
  <div>
    <!-- Header -->
    <div class="d-flex justify-space-between align-center my-4">
      <span class="text-h6 text-sm-h4">Дашборды</span>
      <v-btn color="primary" @click="openCreateDialog">
        <v-icon left>mdi-plus</v-icon>
        Новый дашборд
      </v-btn>
    </div>

    <!-- Empty state -->
    <div
      v-if="dashboards.length === 0 && !loading"
      class="d-flex flex-column align-center py-12 grey--text"
    >
      <v-icon size="64" color="grey lighten-2"
        >mdi-view-dashboard-outline</v-icon
      >
      <div class="text-subtitle-1 mt-3">Нет дашбордов</div>
      <div class="text-caption">Создайте первый дашборд, нажав кнопку выше</div>
    </div>

    <!-- Dashboard tabs -->
    <template v-if="dashboards.length > 0">
      <div class="d-flex align-center mb-4" style="gap: 8px; flex-wrap: wrap">
        <v-chip
          v-for="db in dashboards"
          :key="db.id"
          :color="selectedDashboardId === db.id ? 'primary' : ''"
          :text-color="selectedDashboardId === db.id ? 'white' : ''"
          @click="selectDashboard(db.id)"
          class="cursor-pointer"
          label
        >
          {{ db.name }}
          <v-icon right small @click.stop="confirmDeleteDashboard(db)"
            >mdi-close</v-icon
          >
        </v-chip>
      </div>

      <!-- Selected dashboard -->
      <template v-if="selectedDashboard">
        <div class="d-flex justify-space-between align-center mb-4">
          <span class="text-subtitle-1 grey--text">{{
            selectedDashboard.name
          }}</span>
          <v-btn small outlined color="primary" @click="openAddWidgetDialog">
            <v-icon left small>mdi-widgets-outline</v-icon>
            Добавить виджет
          </v-btn>
        </div>

        <!-- Widgets empty state -->
        <div
          v-if="selectedDashboard.widgets.length === 0"
          class="d-flex flex-column align-center py-8 grey--text"
        >
          <v-icon size="48" color="grey lighten-2">mdi-widgets-outline</v-icon>
          <div class="text-caption mt-2">
            Нет виджетов. Добавьте виджет с помощью кнопки выше.
          </div>
        </div>

        <!-- Widgets grid -->
        <v-row v-else>
          <v-col
            v-for="(widget, index) in selectedDashboard.widgets"
            :key="index"
            cols="12"
            :md="widget.type === 'calls_statistics' ? 8 : 6"
          >
            <div class="widget-wrapper">
              <calls-statistics-widget
                v-if="widget.type === 'calls_statistics'"
              />
              <sales-trend-widget v-else-if="widget.type === 'sales_trend'" />
              <div class="widget-remove-btn">
                <v-btn icon x-small color="grey" @click="removeWidget(index)">
                  <v-icon x-small>mdi-close</v-icon>
                </v-btn>
              </div>
            </div>
          </v-col>
        </v-row>
      </template>
    </template>

    <!-- Create dashboard dialog -->
    <v-dialog
      v-model="createDialog"
      max-width="400"
      content-class="rounded-xl ma-2 ma-sm-6"
    >
      <v-card>
        <v-card-title>Новый дашборд</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="newDashboardName"
            label="Название"
            outlined
            dense
            autofocus
            @keyup.enter="createDashboard"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="createDialog = false">Отмена</v-btn>
          <v-btn color="primary" @click="createDashboard" :loading="saving"
            >Создать</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add widget dialog -->
    <v-dialog
      v-model="addWidgetDialog"
      max-width="480"
      content-class="rounded-xl ma-2 ma-sm-6"
    >
      <v-card>
        <v-card-title>Добавить виджет</v-card-title>
        <v-card-text>
          <v-list>
            <v-list-item
              v-for="wt in availableWidgetTypes"
              :key="wt.type"
              @click="addWidget(wt.type)"
              class="widget-type-item rounded-lg mb-2"
              style="border: 1px solid #e0e0e0"
            >
              <v-list-item-icon>
                <v-icon color="primary">{{ wt.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ wt.label }}</v-list-item-title>
                <v-list-item-subtitle>{{
                  wt.description
                }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="addWidgetDialog = false">Отмена</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirm delete dialog -->
    <v-dialog
      v-model="deleteDialog"
      max-width="360"
      content-class="rounded-xl ma-2 ma-sm-6"
    >
      <v-card>
        <v-card-title>Удалить дашборд?</v-card-title>
        <v-card-text>
          Дашборд
          <strong>{{ dashboardToDelete && dashboardToDelete.name }}</strong>
          будет удалён без возможности восстановления.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog = false">Отмена</v-btn>
          <v-btn color="error" @click="deleteDashboard" :loading="deleting"
            >Удалить</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <error-message-wrapper ref="error_wrapper" />
  </div>
</template>

<script>
import NSApi from "@/api/NSApi";
import CallsStatisticsWidget from "@/components/widgets/CallsStatisticsWidget.vue";
import SalesTrendWidget from "@/components/widgets/SalesTrendWidget.vue";
import ErrorMessageWrapper from "@/components/ErrorMessageWrapper.vue";

const WIDGET_TYPES = [
  {
    type: "calls_statistics",
    label: "Статистика диалогов",
    icon: "mdi-message-text-outline",
    description: "Диалоги по источникам, статусам и операторам из Bitrix",
  },
  {
    type: "sales_trend",
    label: "Тренд продаж",
    icon: "mdi-trending-up",
    description: "График динамики продаж (в разработке)",
  },
];

export default {
  name: "AdminDashboardView",
  components: { CallsStatisticsWidget, SalesTrendWidget, ErrorMessageWrapper },
  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      dashboards: [],
      selectedDashboardId: null,
      createDialog: false,
      newDashboardName: "",
      addWidgetDialog: false,
      deleteDialog: false,
      dashboardToDelete: null,
      availableWidgetTypes: WIDGET_TYPES,
    };
  },
  computed: {
    selectedDashboard() {
      return (
        this.dashboards.find((d) => d.id === this.selectedDashboardId) || null
      );
    },
  },
  methods: {
    async loadDashboards() {
      this.loading = true;
      try {
        this.dashboards = await NSApi.instance.getDashboards();
        if (this.dashboards.length > 0 && !this.selectedDashboardId) {
          this.selectedDashboardId = this.dashboards[0].id;
        }
      } catch (e) {
        this.$refs.error_wrapper && this.$refs.error_wrapper.showError(e);
      } finally {
        this.loading = false;
      }
    },
    selectDashboard(id) {
      this.selectedDashboardId = id;
    },
    openCreateDialog() {
      this.newDashboardName = "";
      this.createDialog = true;
    },
    async createDashboard() {
      if (!this.newDashboardName.trim()) return;
      this.saving = true;
      try {
        const created = await NSApi.instance.createDashboard(
          this.newDashboardName.trim()
        );
        this.dashboards.push(created);
        this.selectedDashboardId = created.id;
        this.createDialog = false;
        this.newDashboardName = "";
      } catch (e) {
        this.$refs.error_wrapper && this.$refs.error_wrapper.showError(e);
      } finally {
        this.saving = false;
      }
    },
    confirmDeleteDashboard(db) {
      this.dashboardToDelete = db;
      this.deleteDialog = true;
    },
    async deleteDashboard() {
      if (!this.dashboardToDelete) return;
      this.deleting = true;
      try {
        await NSApi.instance.deleteDashboard(this.dashboardToDelete.id);
        this.dashboards = this.dashboards.filter(
          (d) => d.id !== this.dashboardToDelete.id
        );
        if (this.selectedDashboardId === this.dashboardToDelete.id) {
          this.selectedDashboardId =
            this.dashboards.length > 0 ? this.dashboards[0].id : null;
        }
        this.deleteDialog = false;
        this.dashboardToDelete = null;
      } catch (e) {
        this.$refs.error_wrapper && this.$refs.error_wrapper.showError(e);
      } finally {
        this.deleting = false;
      }
    },
    openAddWidgetDialog() {
      this.addWidgetDialog = true;
    },
    async addWidget(type) {
      if (!this.selectedDashboard) return;
      const widgets = [...this.selectedDashboard.widgets, { type }];
      try {
        const updated = await NSApi.instance.updateDashboard(
          this.selectedDashboard.id,
          { widgets }
        );
        const idx = this.dashboards.findIndex((d) => d.id === updated.id);
        if (idx !== -1) this.$set(this.dashboards, idx, updated);
      } catch (e) {
        this.$refs.error_wrapper && this.$refs.error_wrapper.showError(e);
      }
      this.addWidgetDialog = false;
    },
    async removeWidget(index) {
      if (!this.selectedDashboard) return;
      const widgets = this.selectedDashboard.widgets.filter(
        (_, i) => i !== index
      );
      try {
        const updated = await NSApi.instance.updateDashboard(
          this.selectedDashboard.id,
          { widgets }
        );
        const idx = this.dashboards.findIndex((d) => d.id === updated.id);
        if (idx !== -1) this.$set(this.dashboards, idx, updated);
      } catch (e) {
        this.$refs.error_wrapper && this.$refs.error_wrapper.showError(e);
      }
    },
  },
  mounted() {
    this.loadDashboards();
  },
};
</script>

<style scoped>
.widget-wrapper {
  position: relative;
}
.widget-remove-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
}
.cursor-pointer {
  cursor: pointer;
}
</style>
